///
/// EarthBrowser
///
/// Webcam Data Provider
///
/// Provides webcam data from the local dataset sync database with custom rendering
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///

import { datasetUrl } from '@/utils/datasetUrl'
import type { WebcamEntry } from '@shared-types/webcam'
import { formatDistanceToNow } from 'date-fns'
import { Play } from 'lucide-react'
import type { ColumnMetadata, DataQuery, DataRow, DataStats, FilterGroup, IDataProvider, ProviderCapabilities, QueryResult } from './types'

// Explicitly convert a filter condition value to text for string comparisons
function conditionText(value: unknown): string {
	if (typeof value === 'string') return value
	if (value === null || value === undefined) return String(value)
	if (typeof value === 'number' || typeof value === 'boolean') return String(value)
	if (value instanceof Date) return value.toString()
	if (Array.isArray(value)) return value.map(v => conditionText(v)).join(',')
	return JSON.stringify(value)
}

const categoryColors: Record<string, string> = {
	traffic: 'bg-slate-500/20 text-slate-300 border-slate-400/30',
	scenic: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
	weather: 'bg-sky-500/20 text-sky-300 border-sky-400/30',
	marine: 'bg-teal-500/20 text-teal-300 border-teal-400/30',
	aviation: 'bg-violet-500/20 text-violet-300 border-violet-400/30',
	wildlife: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
}

function CategoryBadge({ category }: { category: string }) {
	const colors = categoryColors[category] ?? categoryColors.scenic
	return (
		<span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${colors}`}>
			{category}
		</span>
	)
}

export class WebcamDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	private queryFn: () => Promise<WebcamEntry[]>
	private cachedData: WebcamEntry[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		queryFn: () => Promise<WebcamEntry[]>
	}) {
		this.id = config.id
		this.name = config.name
		this.queryFn = config.queryFn
	}

	async initialize(): Promise<void> {
		if (this.isInitializedFlag) return
		this.cachedData = await this.queryFn()
		this.isInitializedFlag = true
	}

	async refreshData(): Promise<void> {
		this.cachedData = await this.queryFn()
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'name',
			name: 'Camera',
			priority: 1,
			minWidth: 200,
			type: 'string',
			nullable: false,
			render: (value: unknown, row: DataRow) => {
				const webcam = row as unknown as WebcamEntry
				// Thumbnail cache-busted by last ingest success so it stays
				// stable between refreshes instead of re-downloading every render
				const stamp = webcam.last_success ? new Date(webcam.last_success).getTime() : 0
				return (
					<div className="flex items-center gap-2.5 min-w-0">
						<div className="relative w-16 h-9 shrink-0 rounded overflow-hidden bg-slate-800 border border-white/10">
							<img
								src={`${datasetUrl(webcam.thumb_url)}?eb=${stamp}`}
								alt=""
								loading="lazy"
								className="absolute inset-0 w-full h-full object-cover"
							/>
							{webcam.stream_url && (
								<div className="absolute inset-0 flex items-center justify-center bg-black/25">
									<Play className="w-3 h-3 text-white/90" fill="currentColor" />
								</div>
							)}
						</div>
						<span className="font-medium truncate max-w-[240px]" title={value as string}>
							{value as string}
						</span>
					</div>
				)
			}
		},
		{
			id: 'category',
			name: 'Category',
			priority: 3,
			minWidth: 110,
			type: 'string',
			nullable: false,
			render: (value: unknown) => <CategoryBadge category={value as string} />
		},
		{
			id: 'region',
			name: 'Region',
			priority: 2,
			minWidth: 140,
			type: 'string',
			nullable: true,
			render: (_value: unknown, row: DataRow) => {
				const webcam = row as unknown as WebcamEntry
				const text = [webcam.region, webcam.country_code].filter(Boolean).join(', ')
				return text
					? <span className="text-white/60 text-xs">{text}</span>
					: <span className="text-white/30">—</span>
			}
		},
		{
			id: 'attribution',
			name: 'Source',
			priority: 4,
			minWidth: 130,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<span className="text-white/60 text-xs truncate max-w-[160px]" title={value as string}>
					{value as string}
				</span>
			)
		},
		{
			id: 'last_success',
			name: 'Updated',
			priority: 3,
			minWidth: 120,
			type: 'string',
			nullable: true,
			render: (value: unknown, row: DataRow) => {
				const webcam = row as unknown as WebcamEntry
				if (!value) return <span className="text-white/30">—</span>
				const isStale = webcam.status === 'stale'
				return (
					<span className={`text-xs ${isStale ? 'text-amber-400/80' : 'text-white/60'}`}>
						{formatDistanceToNow(new Date(value as string), { addSuffix: true })}
						{isStale && ' (stale)'}
					</span>
				)
			}
		}
	]

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async query(query: DataQuery): Promise<QueryResult> {
		let data = [...this.cachedData]

		if (query.filters) {
			data = data.filter(webcam => this.rowMatchesFilterGroup(webcam, query.filters!))
		}

		if (query.search) {
			const searchLower = query.search.query.toLowerCase()
			data = data.filter(webcam =>
				webcam.name.toLowerCase().includes(searchLower) ||
				webcam.id.toLowerCase().includes(searchLower) ||
				webcam.category.toLowerCase().includes(searchLower) ||
				webcam.attribution.toLowerCase().includes(searchLower) ||
				(webcam.region?.toLowerCase().includes(searchLower) ?? false) ||
				(webcam.country_code?.toLowerCase().includes(searchLower) ?? false)
			)
		}

		const sort = query.sort && query.sort.length > 0
			? query.sort
			: [{ column: 'name', direction: 'asc' as const }]
		{
			const sortSpec = sort[0]
			data.sort((a, b) => {
				const aVal = this.getColumnValue(a, sortSpec.column)
				const bVal = this.getColumnValue(b, sortSpec.column)

				if (aVal === null || aVal === undefined) return 1
				if (bVal === null || bVal === undefined) return -1

				const compareResult = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
				return sortSpec.direction === 'asc' ? compareResult : -compareResult
			})
		}

		const total = data.length
		const offset = query.offset || 0
		const limit = query.limit || 100
		const paginatedData = data.slice(offset, offset + limit)

		return {
			rows: paginatedData as unknown as DataRow[],
			totalCount: total,
			offset,
			limit,
			hasMore: offset + limit < total,
		}
	}

	private getColumnValue(webcam: WebcamEntry, columnId: string): string | number | null | undefined {
		switch (columnId) {
			case 'name':
				return webcam.name
			case 'category':
				return webcam.category
			case 'region':
				return webcam.region
			case 'attribution':
				return webcam.attribution
			case 'last_success':
				return webcam.last_success
			default:
				return null
		}
	}

	private rowMatchesFilterGroup(webcam: WebcamEntry, filterGroup: FilterGroup): boolean {
		const results = filterGroup.conditions.map((condition) => {
			if ('conditions' in condition) {
				return this.rowMatchesFilterGroup(webcam, condition)
			}

			const value = this.getColumnValue(webcam, condition.column)

			switch (condition.operator) {
				case 'equals':
					return value === condition.value
				case 'not_equals':
					return value !== condition.value
				case 'contains':
					return String(value).toLowerCase().includes(conditionText(condition.value).toLowerCase())
				case 'starts_with':
					return String(value).toLowerCase().startsWith(conditionText(condition.value).toLowerCase())
				case 'ends_with':
					return String(value).toLowerCase().endsWith(conditionText(condition.value).toLowerCase())
				case 'greater_than':
					return Number(value) > Number(condition.value)
				case 'less_than':
					return Number(value) < Number(condition.value)
				case 'greater_than_or_equal':
					return Number(value) >= Number(condition.value)
				case 'less_than_or_equal':
					return Number(value) <= Number(condition.value)
				case 'is_null':
					return value === null || value === undefined
				case 'is_not_null':
					return value !== null && value !== undefined
				default:
					return true
			}
		})

		return filterGroup.operator === 'AND'
			? results.every((r: boolean) => r)
			: results.some((r: boolean) => r)
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.cachedData.length,
			columnCount: this.columnMetadata.length,
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		return this.cachedData.length
	}

	async insert(_rows: DataRow[]): Promise<void> {
		throw new Error('Insert not supported for webcam data provider')
	}

	async update(_filters: FilterGroup, _updates: Partial<DataRow>): Promise<number> {
		throw new Error('Update not supported for webcam data provider')
	}

	async delete(_filters: FilterGroup): Promise<number> {
		throw new Error('Delete not supported for webcam data provider')
	}

	async executeRaw(_sql: string): Promise<QueryResult> {
		throw new Error('Raw SQL not supported for webcam data provider')
	}

	isInitialized(): boolean {
		return this.isInitializedFlag
	}

	async close(): Promise<void> {
		this.cachedData = []
		this.isInitializedFlag = false
	}
}
